<template>
  <div>
    <AppMenu />

    <div tabindex="0" class="cierre-pagina">
      <div class="cierre-contenedor">
        <ValidationObserver v-slot="{ invalid }" ref="form">
          <form class="v-form" @submit.prevent>
            <div class="cierre-encabezado">
              <div>
                <h1 class="cierre-encabezado__titulo">{{ $t("close_turn") }}</h1>
                <div class="cierre-encabezado__sub" v-if="turnoDesde()">Turno abierto desde {{ turnoDesde() }}</div>
              </div>
            </div>
            <!-- Turno ya cerrado: sólo queda imprimir el cuadre. -->
            <div v-if="print" id="cierre-cerrado" class="cierre-tarjeta cierre-cerrado">
              <v-icon size="56" color="success">mdi-check-circle</v-icon>
              <h2 class="mt-2">Turno cerrado</h2>
              <p class="grey--text text--darken-1">El cuadre quedó guardado. Imprime el comprobante para archivarlo.</p>
              <v-btn color="primary" large depressed class="mr-2" @click="printTurn()"><v-icon left>mdi-printer</v-icon>{{ $t("print") }}</v-btn>
              <v-btn outlined large color="primary" to="/">Salir</v-btn>
            </div>

            <!-- Sin turno abierto no hay nada que contar (al recargar, el turno tarda un instante en llegar). -->
            <div v-else-if="!$store.getters.turn" id="cierre-sin-turno" class="cierre-tarjeta cierre-cerrado">
              <v-icon size="56" color="grey">mdi-cash-register</v-icon>
              <h2 class="mt-2">No hay un turno abierto</h2>
              <p class="grey--text text--darken-1">Abre un turno desde el POS para poder cuadrar la caja.</p>
              <v-btn color="primary" large depressed to="/pos">Ir al POS</v-btn>
            </div>

            <div v-else class="cierre-cuerpo">
              <div class="cierre-principal">
                <div class="cierre-pasos" role="tablist">
                  <button
                    v-for="p in pasos()"
                    :key="p.id"
                    type="button"
                    role="tab"
                    class="cierre-paso"
                    :class="['cierre-paso--' + estadoPaso(p.id), { 'cierre-paso--activo': paso === p.id }]"
                    :aria-selected="paso === p.id ? 'true' : 'false'"
                    @click="irAPaso(p.id)"
                  >
                    <span class="cierre-paso__num">
                      <v-icon v-if="estadoPaso(p.id) === 'ok'" small color="white">mdi-check</v-icon>
                      <v-icon v-else-if="estadoPaso(p.id) === 'alerta'" small color="white">mdi-exclamation-thick</v-icon>
                      <template v-else>{{ p.id }}</template>
                    </span>
                    <span class="cierre-paso__txt">
                      <strong>{{ p.titulo }}</strong>
                      <small>{{ p.detalle }}</small>
                    </span>
                  </button>
                </div>

                <!-- PASO 1: lo que impide o afecta el cierre -->
                <section v-show="paso === 1" class="cierre-tarjeta">
                  <div v-if="totalOpenedOrders > 0" class="cierre-bloqueo">
                    <v-icon color="red darken-1" class="mr-3">mdi-alert-circle</v-icon>
                    <div>
                      <strong>No se puede cerrar con {{ totalOpenedOrders }} {{ totalOpenedOrders === 1 ? "orden abierta" : "órdenes abiertas" }}.</strong>
                      <div class="caption">Cóbralas o anúlalas desde «Pedidos pendientes» y vuelve aquí.</div>
                    </div>
                    <v-btn small outlined color="red darken-1" to="/pos" class="ml-auto">Ir al POS</v-btn>
                  </div>
                  <div v-else class="cierre-ok">
                    <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                    No hay órdenes abiertas: el turno se puede cerrar.
                  </div>

                  <v-expansion-panels class="cierre-paneles" flat>
                    <v-expansion-panel v-if="openedList.length > 0">
                      <v-expansion-panel-header>
                        <span><strong>Órdenes abiertas ({{ openedList.length }})</strong> <span class="grey--text text--darken-1">· {{ $filters.currency(totalAbiertas(), 0) }}</span></span>
                        <span class="cierre-chip-alerta">Bloquean el cierre</span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <div class="cierre-dos-columnas">
                          <div v-for="o in openedList" :key="o.id" class="cierre-fila">
                            <div>
                              <strong>{{ o.name }}</strong><span v-if="o.seller"> · {{ o.seller }}</span>
                              <div class="caption grey--text text--darken-1">{{ estadoPedido(o.status) }}</div>
                            </div>
                            <strong>{{ $filters.currency(o.total, 0) }}</strong>
                          </div>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <v-expansion-panel v-if="expenses_list.length > 0">
                      <v-expansion-panel-header>
                        <span><strong>Egresos ({{ expenses_list.length }})</strong> <span class="grey--text text--darken-1">· {{ $filters.currency(total_expenses, 0) }}</span></span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <div v-for="e in expenses_list" :key="e.id" class="cierre-fila">
                          <div>
                            <strong>{{ e.consecutive || "Egreso" }}</strong><span v-if="e.to"> · {{ e.to }}</span>
                            <div class="caption grey--text text--darken-1">{{ (e.methods || []).join(", ") }}<span v-if="e.note"> · {{ e.note }}</span></div>
                          </div>
                          <strong>{{ $filters.currency(e.total, 0) }}</strong>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <v-expansion-panel v-if="credit_notes.length > 0">
                      <v-expansion-panel-header>
                        <span><strong>Notas crédito ({{ credit_notes.length }})</strong> <span class="grey--text text--darken-1">· {{ $filters.currency(total_credit_note, 0) }}</span></span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <div v-for="n in credit_notes" :key="n.id" class="cierre-fila">
                          <div>
                            <strong>{{ n.consecutive || "Nota crédito" }}</strong>
                            <div class="caption grey--text text--darken-1" v-if="n.note">{{ n.note }}</div>
                          </div>
                          <strong>{{ $filters.currency(n.total, 0) }}</strong>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <v-expansion-panel v-if="anuladas.length > 0">
                      <v-expansion-panel-header>
                        <span><strong>Órdenes anuladas ({{ anuladas.length }})</strong> <span class="grey--text text--darken-1">· {{ $filters.currency(totalAnuladas(), 0) }}</span></span>
                        <span v-if="anuladasSinMotivo() > 0" class="cierre-chip-alerta">{{ anuladasSinMotivo() }} sin motivo</span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <div class="cierre-dos-columnas">
                          <div v-for="a in anuladas" :key="a.id" class="cierre-fila">
                            <div>
                              <strong>{{ a.name }}</strong><span v-if="a.by"> · {{ a.by }}</span>
                              <div class="caption" :class="a.reason ? 'grey--text text--darken-1' : 'red--text font-weight-bold'">
                                {{ a.reason || "Sin motivo" }}<span v-if="a.with_payments" class="red--text font-weight-bold"> · tenía pagos</span>
                              </div>
                            </div>
                            <strong>{{ $filters.currency(a.total, 0) }}</strong>
                          </div>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <v-expansion-panel v-if="discounts && discounts.length > 0 && total_discounts > 0">
                      <v-expansion-panel-header>
                        <span><strong>Descuentos por producto</strong> <span class="grey--text text--darken-1">· {{ $filters.currency(total_discounts, 0) }}</span></span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <template v-for="(discount, index) in discounts">
                          <div v-if="discount.total > 0" :key="'discount_detail_' + index" class="cierre-fila">
                            <strong>{{ discount.product_name }}</strong>
                            <strong>{{ $filters.currency(discount.total, 0) }}</strong>
                          </div>
                        </template>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                  <div v-if="!hayNovedades()" class="caption grey--text text--darken-1 mt-3">Sin egresos, notas crédito, descuentos ni anulaciones en este turno.</div>

                  <div class="cierre-navegacion">
                    <v-btn outlined color="primary" to="/pos">{{ $t("cancel") }}</v-btn>
                    <v-btn color="primary" depressed @click="irAPaso(2)">Continuar a contar<v-icon right>mdi-arrow-right</v-icon></v-btn>
                  </div>
                </section>

                <!-- PASO 2: contar cada medio de pago -->
                <section v-show="paso === 2" class="cierre-tarjeta">
                  <p class="cierre-ayuda">Cuenta lo que hay en cada medio de pago y escríbelo. La pantalla te dice al instante si falta o sobra.</p>
                  <div class="cierre-medios">
                    <div
                      v-for="item in payments"
                      :key="item.id"
                      class="cierre-medio"
                      :class="{ 'cierre-medio--efectivo': esEfectivo(item) }"
                      :style="{ '--c': colorDe(item).c, '--cl': colorDe(item).l }"
                    >
                      <div class="cierre-medio__cabecera">
                        <span class="cierre-medio__icono"><v-icon color="white">{{ colorDe(item).icono }}</v-icon></span>
                        <div class="cierre-medio__titulo">
                          <strong>{{ item.name }}</strong>
                          <small>Ventas {{ $filters.currency(item.system_value, 0) }}</small>
                        </div>
                        <div class="cierre-medio__esperado">
                          <small>Debe haber</small>
                          <strong>{{ $filters.currency(esperadoDe(item), 0) }}</strong>
                        </div>
                      </div>

                      <div class="cierre-medio__cuerpo">
                        <div class="cierre-medio__detalle">
                          <template v-if="esEfectivo(item)">
                            <div class="cierre-linea">
                              <span>Ventas en efectivo</span><strong>{{ $filters.currency(item.system_value, 0) }}</strong>
                            </div>
                            <div
                              class="cierre-linea"
                              :class="{ 'cierre-linea--mas': baseEsperada() > 0, 'cierre-linea--apagada': tieneBase() && base_contada === false }"
                            >
                              <span>
                                + Base inicial
                                <em v-if="tieneBase() && base_contada === false">(no la contaste)</em>
                                <em v-else-if="tieneBase() && base_contada === true">(contada)</em>
                              </span>
                              <strong>{{ $filters.currency(baseDelTurno(), 0) }}</strong>
                            </div>
                            <div v-if="expensesOf(item.name) > 0" class="cierre-linea cierre-linea--menos">
                              <span>− Egresos</span><strong>{{ $filters.currency(expensesOf(item.name), 0) }}</strong>
                            </div>
                            <div v-if="nc_efectivo === true && total_credit_note > 0" class="cierre-linea cierre-linea--menos">
                              <span>− Notas crédito devueltas</span><strong>{{ $filters.currency(total_credit_note, 0) }}</strong>
                            </div>
                          </template>
                          <div v-else-if="expensesOf(item.name) > 0" class="cierre-linea cierre-linea--info">
                            <span>Egresos con este medio (no salen del cajón)</span><span>{{ $filters.currency(expensesOf(item.name), 0) }}</span>
                          </div>
                          <div v-else class="cierre-medio__nota">Cuenta los comprobantes o abonos de este medio.</div>

                          <!-- Sin esta pregunta, la base que no se contaba salía como faltante (584 de 984 cierres con diferencia en 2026). -->
                          <div v-if="esEfectivo(item) && tieneBase()" class="cierre-pregunta">
                            <div class="font-weight-medium mb-1">
                              ¿Contaste también la base de {{ $filters.currency($store.getters.turn.starting_amount, 0) }}?
                            </div>
                            <v-radio-group v-model="base_contada" row dense hide-details class="mt-0 pt-0 input-turn-base-counted">
                              <v-radio label="Sí, está en lo que conté" :value="true"></v-radio>
                              <v-radio label="No, la retiré o la dejé aparte" :value="false"></v-radio>
                            </v-radio-group>
                          </div>

                          <!-- Una nota crédito no dice con qué medio se devolvió el dinero: lo dice quien cierra. -->
                          <div v-if="esEfectivo(item) && total_credit_note > 0" class="cierre-pregunta">
                            <div class="font-weight-medium mb-1">
                              Hubo notas crédito por {{ $filters.currency(total_credit_note, 0) }}: ¿devolviste ese dinero en efectivo del cajón?
                            </div>
                            <v-radio-group v-model="nc_efectivo" row dense hide-details class="mt-0 pt-0 input-turn-credit-note-cash">
                              <v-radio label="Sí, salió del cajón" :value="true"></v-radio>
                              <v-radio label="No, se devolvió de otra forma" :value="false"></v-radio>
                            </v-radio-group>
                          </div>
                        </div>

                        <div class="cierre-medio__conteo">
                          <ng-number
                            class="fix_input_margin_top"
                            v-model="item.counted_value"
                            :label="$t('counted_value')"
                            rules="required"
                            show-required="true"
                          ></ng-number>
                          <div v-if="resultadoDe(item)" class="cierre-resultado" :class="'cierre-resultado--' + resultadoDe(item).tipo">
                            <v-icon small left>{{ resultadoDe(item).icono }}</v-icon>{{ resultadoDe(item).texto }}
                          </div>
                          <div v-else class="cierre-resultado cierre-resultado--pendiente">Escribe lo que contaste</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="cierre-navegacion">
                    <v-btn outlined color="primary" @click="irAPaso(1)"><v-icon left>mdi-arrow-left</v-icon>Atrás</v-btn>
                    <v-btn color="primary" depressed @click="irAPaso(3)">Continuar a cerrar<v-icon right>mdi-arrow-right</v-icon></v-btn>
                  </div>
                </section>

                <!-- PASO 3: revisar y cerrar -->
                <section v-show="paso === 3" class="cierre-tarjeta">
                  <div class="cierre-final" :class="'cierre-resultado--' + resultadoTotal().tipo">
                    <span>{{ resultadoTotal().titulo }}</span>
                    <strong>{{ resultadoTotal().valor }}</strong>
                  </div>

                  <ul class="cierre-pendientes" v-if="pendientesDeCierre().length > 0">
                    <li v-for="(t, i) in pendientesDeCierre()" :key="i">
                      <v-icon small color="orange darken-2" class="mr-1">mdi-alert</v-icon>{{ t.texto }}
                      <a v-if="t.enlace" href="#" @click.prevent="irAPaso(t.paso)">{{ t.enlace }}</a>
                    </li>
                  </ul>
                  <div v-else class="cierre-ok mt-3">
                    <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>Todo listo para cerrar.
                  </div>

                  <div class="mt-4" v-if="totalOpenedOrders == 0">
                    <ng-textarea
                      v-model="item.observation"
                      :label="$t('observation')"
                      :filled="false"
                      :dense="false"
                      rules="required"
                    ></ng-textarea>
                  </div>

                  <div class="cierre-navegacion">
                    <v-btn outlined color="primary" @click="irAPaso(2)"><v-icon left>mdi-arrow-left</v-icon>Atrás</v-btn>
                    <v-btn
                      color="primary"
                      large
                      depressed
                      @click="pedirCierre()"
                      :disabled="invalid || openedOrders || faltaResponder() || totalOpenedOrders > 0"
                    >
                      {{ $t("close_turn") }}
                    </v-btn>
                  </div>
                </section>
              </div>

              <!-- Resumen siempre a la vista, con lo contado hasta ahora -->
              <aside class="cierre-lateral" v-if="$store.getters.turn">
                <div class="cierre-lateral__caja">
                  <h3 class="cierre-lateral__titulo">Resumen del turno</h3>
                  <div class="cierre-lateral__resultado" :class="'cierre-resultado--' + resultadoTotal().tipo">
                    <span>{{ resultadoTotal().titulo }}</span><strong :class="{ 'cierre-mixto': resultadoTotal().mixto }">{{ resultadoTotal().valor }}</strong>
                  </div>
                  <div class="cierre-datos">
                    <div><span>Ventas</span><strong>{{ $filters.currency(totalInTurn(), 0) }}</strong></div>
                    <div><span>Debe haber</span><strong>{{ $filters.currency(totalEsperado(), 0) }}</strong></div>
                    <div><span>Contado</span><strong>{{ $filters.currency(totalContado(), 0) }}</strong></div>
                  </div>
                  <div class="cierre-datos cierre-datos--secundario">
                    <div><span>Domicilios</span><strong>{{ $filters.currency(total_delivery, 0) }}</strong></div>
                    <div><span>Propinas</span><strong>{{ $filters.currency(total_tips, 0) }}</strong></div>
                    <div><span>Descuentos</span><strong>{{ $filters.currency(total_discounts, 0) }}</strong></div>
                    <div><span>Egresos</span><strong>{{ $filters.currency(total_expenses, 0) }}</strong></div>
                    <div><span>Notas crédito</span><strong>{{ $filters.currency(total_credit_note, 0) }}</strong></div>
                    <div v-if="gift_cards_sold > 0" title="Saldo a favor del cliente: entra al cajón pero no es una venta">
                      <span>Tarjetas regalo vendidas</span><strong>{{ $filters.currency(gift_cards_sold, 0) }}</strong>
                    </div>
                    <div v-if="paid_with_gift_card > 0" title="Venta pagada con saldo de una tarjeta: no entró dinero al cajón">
                      <span>Pagado con tarjeta regalo</span><strong>{{ $filters.currency(paid_with_gift_card, 0) }}</strong>
                    </div>
                    <div :class="{ 'cierre-datos--alerta': anuladas.length > 0 }">
                      <span>Anuladas</span><strong>{{ anuladas.length }} · {{ $filters.currency(totalAnuladas(), 0) }}</strong>
                    </div>
                    <template v-if="activePoints()">
                      <div><span>{{ $t("total_discounts_points") }}</span><strong>{{ $filters.currency(total_discounts_points, 0) }}</strong></div>
                      <div><span>{{ $t("total_points_used") }}</span><strong>{{ total_points_used }}</strong></div>
                    </template>
                  </div>
                </div>
              </aside>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </div>
    <v-dialog v-model="confirmarDiferencia" max-width="520" persistent>
      <v-card>
        <v-card-title class="text-h6">
          {{ tituloDeLaDiferencia() }}
        </v-card-title>
        <v-card-text>
          Es una diferencia grande. Antes de cerrar, revisa lo que contaste en cada medio de pago:
          los miles se pueden escribir con o sin punto (199000 o 199.000), y que no falte ni sobre un cero.
          <span v-if="tieneBase()">Revisa también si contaste la base.</span>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn outlined color="primary" @click="confirmarDiferencia = false">Revisar</v-btn>
          <v-btn id="btn-turn-close-anyway" color="primary" @click="confirmarDiferencia = false; closeTurn()">Cerrar de todas formas</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<style scoped>
.fix_input_margin_top {
  margin-top: 0 !important;
}
.cierre-pagina {
  height: calc(100vh - 66px);
  overflow-y: auto;
  background: #f4f6f8;
}
.cierre-contenedor {
  max-width: 1240px;
  margin: 0 auto;
  padding: 20px 24px 40px;
}
.cierre-encabezado {
  margin-bottom: 16px;
}
.cierre-encabezado__titulo {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.2;
}
.cierre-encabezado__sub {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.9rem;
}
.cierre-cuerpo {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 20px;
  align-items: start;
}
.cierre-lateral {
  position: sticky;
  top: 0;
}
.cierre-lateral__caja {
  background: #fff;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
.cierre-lateral__titulo {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 12px;
}
.cierre-lateral__resultado,
.cierre-final {
  display: flex;
  flex-direction: column;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f4f6f8;
  border: 2px solid transparent;
  margin-bottom: 12px;
}
.cierre-lateral__resultado span,
.cierre-final span {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.65);
}
.cierre-lateral__resultado strong {
  font-size: 1.6rem;
}
.cierre-final {
  padding: 18px 20px;
}
.cierre-mixto { font-size: 1.1rem !important; line-height: 1.4; }
.cierre-final strong {
  font-size: 2.2rem;
}
.cierre-resultado--ok { color: #2e7d32; }
.cierre-resultado--falta { color: #c62828; }
.cierre-resultado--sobra { color: #ef6c00; }
.cierre-lateral__resultado.cierre-resultado--ok,
.cierre-final.cierre-resultado--ok { background: #e8f5e9; border-color: #43a047; }
.cierre-lateral__resultado.cierre-resultado--falta,
.cierre-final.cierre-resultado--falta { background: #ffebee; border-color: #e53935; }
.cierre-lateral__resultado.cierre-resultado--sobra,
.cierre-final.cierre-resultado--sobra { background: #fff3e0; border-color: #fb8c00; }
.cierre-datos > div {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 0.92rem;
}
.cierre-datos > div span { color: rgba(0, 0, 0, 0.65); }
.cierre-datos--secundario { margin-top: 14px; }
.cierre-datos--secundario > div { font-size: 0.85rem; }
.cierre-datos--alerta strong { color: #c62828; }

.cierre-pasos {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}
.cierre-paso {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #fff;
  border: 2px solid transparent;
  text-align: left;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}
.cierre-paso--activo { border-color: var(--v-primary-base); }
.cierre-paso__num {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #cfd8dc;
  color: #fff;
  font-weight: 700;
}
.cierre-paso--activo .cierre-paso__num { background: var(--v-primary-base); }
.cierre-paso--ok .cierre-paso__num { background: #43a047; }
.cierre-paso--alerta .cierre-paso__num { background: #e53935; }
.cierre-paso__txt { display: flex; flex-direction: column; line-height: 1.2; min-width: 0; }
.cierre-paso__txt small { color: rgba(0, 0, 0, 0.6); font-size: 0.78rem; }

.cierre-tarjeta {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
.cierre-cerrado { text-align: center; padding: 40px 20px; max-width: 560px; margin: 0 auto; }
.cierre-ayuda { color: rgba(0, 0, 0, 0.65); margin-bottom: 14px; }
.cierre-bloqueo {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-radius: 12px;
  background: #ffebee;
  margin-bottom: 14px;
}
.cierre-ok {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border-radius: 12px;
  background: #e8f5e9;
  margin-bottom: 14px;
}
.cierre-navegacion {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 22px;
}
.cierre-pendientes {
  list-style: none;
  padding: 12px 14px;
  margin: 14px 0 0;
  border-radius: 12px;
  background: #fff8e1;
  font-size: 0.92rem;
}
.cierre-pendientes li { padding: 3px 0; }

.cierre-medios {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
}
.cierre-medio {
  --c: #607d8b;
  --cl: #eceff1;
  border: 1px solid var(--c);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
}
.cierre-medio--efectivo { grid-column: 1 / -1; }
.cierre-medio__cabecera {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: var(--cl);
  border-bottom: 1px solid var(--c);
}
.cierre-medio__icono {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--c);
}
.cierre-medio__titulo { display: flex; flex-direction: column; min-width: 0; line-height: 1.25; }
.cierre-medio__titulo strong { font-size: 1rem; }
.cierre-medio__titulo small { color: rgba(0, 0, 0, 0.6); font-size: 0.8rem; }
.cierre-medio__esperado { margin-left: auto; text-align: right; display: flex; flex-direction: column; line-height: 1.2; }
.cierre-medio__esperado small { color: rgba(0, 0, 0, 0.6); font-size: 0.75rem; }
.cierre-medio__esperado strong { font-size: 1.3rem; color: var(--c); }
.cierre-medio__cuerpo {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  align-items: start;
  gap: 16px;
  padding: 10px 14px 12px;
}
.cierre-medio__nota { color: rgba(0, 0, 0, 0.55); font-size: 0.88rem; }
.cierre-medio__conteo {
  align-self: start;
}
.cierre-medio__conteo >>> input { font-size: 1.25rem; font-weight: 700; }
.cierre-medio__conteo >>> .v-input__slot { margin-bottom: 0 !important; min-height: 44px !important; }
.cierre-medio__conteo >>> .v-input:not(.error--text) .v-text-field__details { display: none; }
.cierre-linea {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 0.95rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.cierre-linea--mas strong { color: #2e7d32; }
.cierre-linea--apagada { color: rgba(0, 0, 0, 0.45); }
.cierre-linea--apagada strong { text-decoration: line-through; }
.cierre-linea em { font-style: normal; font-size: 0.8rem; color: rgba(0, 0, 0, 0.55); margin-left: 4px; }
.cierre-linea--menos strong { color: #c62828; }
.cierre-linea--info { font-size: 0.82rem; color: rgba(0, 0, 0, 0.55); border-bottom: 0; }
.cierre-pregunta {
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  font-size: 0.9rem;
}
.cierre-resultado {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 6px;
  padding: 5px 12px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.92rem;
}
.cierre-resultado--pendiente { background: #eceff1; color: rgba(0, 0, 0, 0.5); font-weight: 500; }
.cierre-medio .cierre-resultado--ok { background: #e8f5e9; }
.cierre-medio .cierre-resultado--falta { background: #ffebee; }
.cierre-medio .cierre-resultado--sobra { background: #fff3e0; }
.cierre-fila {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.cierre-fila:last-child { border-bottom: 0; }
.cierre-dos-columnas {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  column-gap: 28px;
}
.cierre-chip-alerta {
  flex: 0 0 auto;
  margin-left: 10px;
  padding: 2px 10px;
  border-radius: 10px;
  background: #ffebee;
  color: #c62828;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}
.cierre-paneles .v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px !important;
  margin-bottom: 8px;
}
.cierre-paneles .v-expansion-panel::before { box-shadow: none; }
.cierre-paneles .v-expansion-panel-header { min-height: 50px; }

@media (max-width: 1000px) {
  .cierre-cuerpo { grid-template-columns: minmax(0, 1fr); }
  .cierre-medio__cuerpo { grid-template-columns: minmax(0, 1fr); }
  .cierre-lateral { position: static; }
}
@media (max-width: 640px) {
  .cierre-contenedor { padding: 12px 10px 30px; }
  .cierre-paso__txt small { display: none; }
  .cierre-paso { padding: 8px; }
  .cierre-medios { grid-template-columns: minmax(0, 1fr); }
  .cierre-navegacion { flex-wrap: wrap; }
}
</style>
<script>
import AppMenu from "../menu/AppMenu.vue";
export default {
  name: "CloseTurn",
  components: { AppMenu },
  data: () => ({
    key: 0,
    item: { amount: null, observation: "" },
    app_version: null,
    turn: null,
    payments: [],
    value_item: null,
    loading: false,
    openedOrders: false,
    print: false,
    total_delivery: 0,
    total_tips: 0,
    total_discounts_points: 0,
    total_discounts: 0,
    total_points_used: 0,
    difference: 0,
    // ¿La base del turno está en lo contado? null hasta que el cajero responda.
    base_contada: null,
    // ¿Las notas crédito del turno se devolvieron en efectivo del cajón? null hasta que el cajero responda.
    nc_efectivo: null,
    // Paso de la pantalla: 1 pendientes y novedades, 2 contar, 3 cerrar.
    paso: 1,
    expenses_list: [],
    credit_notes: [],
    anuladas: [],
    confirmarDiferencia: false,
    ordersNotSyncro: 0,
    turnToPrint: null,
    totalOpenedOrders: 0,
    openedList: [],
    total_expenses: 0,
    total_credit_note: 0,
    gift_cards_sold: 0,
    paid_with_gift_card: 0,
    expense_details: [],
    discounts: [
    ],
    //cart: null
    //priceList:
  }),

  watch: {
    "$store.getters.turn"(value) {
      if (value) {
        this.turn = value;
      }
    },
    turn(value) {
      if (value) {
        this.load();
      }
    },
  },
  mounted() {
    this.turn = this.$store.getters.turn;
  },
  methods: {
    openHelp() {
      window.open(process.env.VUE_APP_BASECONFIG_URL_HELP, "_blank");
    },
    launchMenuPos(evt) {
      return false;
    },
    async load() {
      // let turn = this.$store.getters.turn;

      if (!this.turn) {
        this.$router.push("/");
        return;
      }
      this.calculateTotals(this.turn);
    },
    estadoPedido(estado) {
      const textos = {
        create: "Por cobrar",
        pending: "Esperando a la caja",
        received: "Recibido en cocina",
        preparing: "En preparación",
        prepared: "Listo, por cobrar",
        returned: "Devuelto al mesero",
      };
      return textos[estado] || "Por cobrar";
    },
    toogleDrawer() {
      this.$store.commit("toggle");
    },
    async calculateTotals(turn) {
      let infoTurn = await this.$pos.getTurnInfo(this.$store.getters.turn.id);
      this.payments = infoTurn.payments;
      this.total_delivery = infoTurn.totals.total_delivery;
      this.total_discounts_points = infoTurn.totals.total_discounts_points;
      this.total_points_used = infoTurn.totals.total_points_used;
      this.total_tips = infoTurn.totals.total_tips;
      this.totalOpenedOrders = infoTurn.totals.total_opened_orders;
      this.openedList = infoTurn.totals.opened_orders || [];
      this.total_expenses = infoTurn.totals.total_expenses;
      this.total_credit_note = infoTurn.totals.total_credit_note;
      this.gift_cards_sold = infoTurn.totals.gift_cards_sold || 0;
      this.paid_with_gift_card = infoTurn.totals.paid_with_gift_card || 0;
      this.total_discounts = infoTurn.totals.total_discounts;
      this.expense_details = infoTurn.expenses;
      this.expenses_list = infoTurn.expenses_list || [];
      this.credit_notes = infoTurn.credit_notes || [];
      this.anuladas = infoTurn.annulled || [];
      this.discounts = infoTurn.discounts;
    },
    activePoints() {
      let activeModulePoints = this.$store.getters.getGeneralValue(
        "gen_others_active_point_accumulation"
      );

      return activeModulePoints;
    },
    totalInTurn() {
      let total = 0;
      for (const i in this.payments) {
        total += parseFloat(this.payments[i].system_value);
      }
      return total;
    },
    // Egresos aprobados del turno pagados con ese medio: salieron del cajón, así que no
    // pueden esperarse en el conteo.
    expensesOf(paymentMethodName) {
      let total = 0;
      for (const e of this.expense_details || []) {
        if (e.name === paymentMethodName) {
          total += parseFloat(e.total) || 0;
        }
      }
      return total;
    },
    // Cada tipo de medio con su color: se distinguen de un vistazo al contar.
    colorDe(item) {
      const n = String(item.name || "").toLowerCase();
      if (this.esEfectivo(item)) return { c: "#2e7d32", l: "#e8f5e9", icono: "mdi-cash-multiple" };
      if (n.includes("tarjeta") || n.includes("datafono") || n.includes("débito") || n.includes("crédito")) {
        if (n.includes("transfer")) return { c: "#6a1b9a", l: "#f3e5f5", icono: "mdi-bank-transfer" };
        return { c: "#1565c0", l: "#e3f2fd", icono: "mdi-credit-card-outline" };
      }
      if (n.includes("transfer") || n.includes("consign") || n.includes("bancari") || n.includes("pse")) {
        return { c: "#6a1b9a", l: "#f3e5f5", icono: "mdi-bank-transfer" };
      }
      if (n.includes("billetera") || n.includes("nequi") || n.includes("daviplata") || n.includes("qr")) {
        return { c: "#ef6c00", l: "#fff3e0", icono: "mdi-wallet-outline" };
      }
      return { c: "#00838f", l: "#e0f7fa", icono: "mdi-cash-check" };
    },
    pasos() {
      return [
        { id: 1, titulo: "Pendientes", detalle: this.totalOpenedOrders > 0 ? this.totalOpenedOrders + " abiertas" : "Todo al día" },
        { id: 2, titulo: "Contar", detalle: "Cada medio de pago" },
        { id: 3, titulo: "Cerrar", detalle: "Revisar y cerrar" },
      ];
    },
    estadoPaso(n) {
      if (n === 1) {
        return this.totalOpenedOrders > 0 ? "alerta" : "ok";
      }
      if (n === 2) {
        const filas = Object.values(this.payments || {});
        return filas.length > 0 && filas.every((p) => this.contado(p)) && !this.faltaResponder() ? "ok" : "pendiente";
      }
      return "pendiente";
    },
    irAPaso(n) {
      this.paso = n;
      this.$nextTick(() => {
        const pagina = this.$el.querySelector(".cierre-pagina");
        if (pagina) {
          pagina.scrollTop = 0;
        }
      });
    },
    turnoDesde() {
      const turn = this.$store.getters.turn;
      return turn && turn.opening_date ? this.$filters.datetime(turn.opening_date) : "";
    },
    totalAbiertas() {
      return this.openedList.reduce((s, o) => s + (parseFloat(o.total) || 0), 0);
    },
    hayNovedades() {
      return (
        this.expenses_list.length > 0 ||
        this.credit_notes.length > 0 ||
        this.anuladas.length > 0 ||
        (this.discounts && this.discounts.length > 0 && this.total_discounts > 0)
      );
    },
    // Lo que todavía impide cerrar, con el enlace al paso donde se resuelve.
    pendientesDeCierre() {
      const lista = [];
      if (this.totalOpenedOrders > 0) {
        lista.push({ texto: this.totalOpenedOrders === 1 ? "Hay 1 orden abierta." : "Hay " + this.totalOpenedOrders + " órdenes abiertas.", enlace: this.totalOpenedOrders === 1 ? "Resolverla" : "Resolverlas", paso: 1 });
      }
      const sinContar = Object.values(this.payments || {}).filter((p) => !this.contado(p));
      if (sinContar.length > 0) {
        lista.push({ texto: "Falta contar: " + sinContar.map((p) => p.name).join(", ") + ".", enlace: "Ir a contar", paso: 2 });
      }
      if (this.faltaResponder()) {
        lista.push({ texto: "Responde las preguntas del efectivo.", enlace: "Ir a contar", paso: 2 });
      }
      if (this.totalOpenedOrders == 0 && !String(this.item.observation || "").trim()) {
        lista.push({ texto: "Escribe una observación (es obligatoria).", enlace: "", paso: 3 });
      }
      return lista;
    },
    esEfectivo(item) {
      return String(item.id) === "10";
    },
    // Lo pagado en efectivo con egresos del turno (el medio de pago «Efectivo» de la empresa).
    egresosEnEfectivo() {
      const fila = Object.values(this.payments || {}).find((p) => this.esEfectivo(p));
      return fila ? this.expensesOf(fila.name) : 0;
    },
    hayFilaEfectivo() {
      return Object.values(this.payments || {}).some((p) => this.esEfectivo(p));
    },
    // La base sólo se espera en el cajón si el cajero dice que la contó.
    baseEsperada() {
      const turn = this.$store.getters.turn;
      const base = turn ? parseFloat(turn.starting_amount) || 0 : 0;
      return this.base_contada !== false ? base : 0;
    },
    ncDevuelta() {
      return this.nc_efectivo === true ? parseFloat(this.total_credit_note) || 0 : 0;
    },
    // Lo que debe haber en ese medio: ventas − egresos (+ base y − notas crédito devueltas, en efectivo).
    esperadoDe(item) {
      let v = parseFloat(item.system_value) || 0;
      // Sólo lo pagado en efectivo sale del cajón; un egreso con tarjeta o transferencia no cambia lo que se cuenta.
      if (this.esEfectivo(item)) {
        v += this.baseEsperada() - this.ncDevuelta() - this.expensesOf(item.name);
      }
      return v;
    },
    totalEsperado() {
      let total = 0;
      for (const i in this.payments) {
        total += this.esperadoDe(this.payments[i]);
      }
      if (!this.hayFilaEfectivo()) {
        total += this.baseEsperada() - this.ncDevuelta();
      }
      return total;
    },
    totalContado() {
      let total = 0;
      for (const i in this.payments) {
        total += this.pesos(this.payments[i].counted_value);
      }
      return total;
    },
    contado(item) {
      const v = item.counted_value;
      return !(v === null || v === undefined || v === "");
    },
    // «Faltan», «Sobran» o «Cuadrado» de un medio, apenas el cajero escribe lo que contó.
    resultadoDe(item) {
      if (!this.contado(item)) {
        return null;
      }
      const d = Math.round(this.esperadoDe(item) - this.pesos(item.counted_value));
      if (d === 0) {
        return { tipo: "ok", icono: "mdi-check-circle", texto: "Cuadrado" };
      }
      return d > 0
        ? { tipo: "falta", icono: "mdi-alert-circle", texto: "Faltan " + this.$filters.currency(d, 0) }
        : { tipo: "sobra", icono: "mdi-information", texto: "Sobran " + this.$filters.currency(-d, 0) };
    },
    // Lo que falta y lo que sobra, medio por medio: un medio que sobra no tapa a otro que falta.
    diferenciasPorMedio() {
      let falta = 0;
      let sobra = 0;
      for (const p of Object.values(this.payments || {})) {
        if (!this.contado(p)) {
          continue;
        }
        const d = Math.round(this.esperadoDe(p) - this.pesos(p.counted_value));
        if (d > 0) falta += d;
        if (d < 0) sobra -= d;
      }
      return { falta, sobra };
    },
    tituloDeLaDiferencia() {
      const { falta, sobra } = this.diferenciasPorMedio();
      const f = this.$filters.currency(falta, 0);
      const s = this.$filters.currency(sobra, 0);
      if (falta > 0 && sobra > 0) return "Faltan " + f + " y sobran " + s;
      return falta > 0 ? "Faltan " + f : "Sobran " + s;
    },
    resultadoTotal() {
      const alguno = Object.values(this.payments || {}).some((p) => this.contado(p));
      if (!alguno) {
        return { tipo: "pendiente", titulo: "Diferencia", valor: "—" };
      }
      const { falta, sobra } = this.diferenciasPorMedio();
      if (falta === 0 && sobra === 0) {
        return { tipo: "ok", titulo: "Cuadrado", valor: this.$filters.currency(0, 0) };
      }
      if (falta > 0 && sobra > 0) {
        return {
          tipo: "falta",
          titulo: "Hay diferencias por medio",
          valor: "Faltan " + this.$filters.currency(falta, 0) + " · Sobran " + this.$filters.currency(sobra, 0),
          mixto: true,
        };
      }
      return falta > 0
        ? { tipo: "falta", titulo: "Faltan", valor: this.$filters.currency(falta, 0) }
        : { tipo: "sobra", titulo: "Sobran", valor: this.$filters.currency(sobra, 0) };
    },
    totalAnuladas() {
      return this.anuladas.reduce((s, a) => s + (parseFloat(a.total) || 0), 0);
    },
    anuladasSinMotivo() {
      return this.anuladas.filter((a) => !a.reason).length;
    },
    // Las preguntas del efectivo (base y notas crédito) se responden antes de cerrar.
    faltaResponder() {
      if (!this.hayFilaEfectivo()) {
        return false;
      }
      return (this.tieneBase() && this.base_contada === null) || (this.total_credit_note > 0 && this.nc_efectivo === null);
    },
    // Total en caja (lo que se guarda como monto final): ventas − egresos + base − notas crédito devueltas.
    totalWithTurn() {
      let total = 0;
      for (const i in this.payments) {
        total += parseFloat(this.payments[i].system_value);
        if (this.esEfectivo(this.payments[i])) {
          total -= this.expensesOf(this.payments[i].name);
        }
      }
      let turn = this.$store.getters.turn;
      if (turn) {
        if (turn.starting_amount) {
          total += parseFloat(turn.starting_amount);
        }
      }
      return total - this.ncDevuelta();
    },
    /**
     * Lo contado, en pesos. «199.000» son ciento noventa y nueve mil: el campo numérico lo tomaba
     * como 199 y el cierre quedaba con un faltante que no existía. Un punto seguido de grupos de
     * tres cifras es separador de miles; la coma, también.
     */
    pesos(valor) {
      if (valor === null || valor === undefined || valor === "") {
        return 0;
      }
      let texto = String(valor).trim().replace(/\s|\$/g, "");
      if (/^\d{1,3}([.,]\d{3})+$/.test(texto)) {
        texto = texto.replace(/[.,]/g, "");
      }
      return parseFloat(texto) || 0;
    },
    baseDelTurno() {
      const turn = this.$store.getters.turn;
      return turn ? parseFloat(turn.starting_amount) || 0 : 0;
    },
    tieneBase() {
      const turn = this.$store.getters.turn;
      return !!(turn && parseFloat(turn.starting_amount) > 0);
    },
    // Con una diferencia grande se pide confirmar: casi siempre es un error al escribir lo contado.
    pedirCierre() {
      this.calcDifference();
      const { falta, sobra } = this.diferenciasPorMedio();
      const dif = falta + sobra;
      if (dif >= Math.max(50000, this.totalWithTurn() * 0.1)) {
        this.confirmarDiferencia = true;
        return;
      }
      this.closeTurn();
    },
    calcDifference() {
      const difference = this.totalEsperado() - this.totalContado();
      this.difference = difference;
      return difference;
    },
    formatPayments() {
      let response = [];
      for (const key in this.payments) {
        if (this.payments.hasOwnProperty(key)) {
          this.payments[key].counted_value = this.pesos(
            this.payments[key].counted_value
          );
          // Para la contabilidad (§16): en el efectivo, si lo contado incluye la base.
          if (this.tieneBase() && String(this.payments[key].id) === "10") {
            this.payments[key].base_counted = this.base_contada;
          }
          // Lo que salió del cajón por notas crédito: la contabilidad lo necesita para el cuadre.
          if (this.esEfectivo(this.payments[key])) {
            this.payments[key].credit_notes_returned = this.ncDevuelta();
          }
          response.push(this.payments[key]);
        }
      }
      return response;
    },
    getData() {
      //delete this.turn.opening_date;
      let turn = this.$store.getters.turn;
      turn.observation = this.item.observation;
      turn.data_payments = this.formatPayments();
      turn.final_amount = this.totalWithTurn();
      turn.difference = this.difference;
      turn.delivery_amount = this.total_delivery;
      turn.total_sales = this.totalInTurn();
      // Para el comprobante impreso del cuadre (no son columnas del turno).
      turn.cash_expenses_amount = this.egresosEnEfectivo();
      turn.other_expenses_amount = Math.max(0, (parseFloat(this.total_expenses) || 0) - this.egresosEnEfectivo());
      turn.credit_notes_amount = parseFloat(this.total_credit_note) || 0;
      turn.gift_cards_sold_amount = parseFloat(this.gift_cards_sold) || 0;
      turn.gift_card_paid_amount = parseFloat(this.paid_with_gift_card) || 0;
      turn.credit_notes_returned_amount = this.ncDevuelta();
      turn.annulled_orders_count = this.anuladas.length;
      turn.annulled_orders_amount = this.totalAnuladas();
      turn.tip_amount = this.total_tips;
      turn.opened = false;
      turn.closing_date = this.$filters.datetime(new Date());
      //this.turn.opening_date = this.$filters.datetime(this.turn.opening_date);
      if (turn.company && turn.company.id) {
        turn.company = turn.company.id;
      }
      if (turn.subsidiary && turn.subsidiary.id) {
        turn.subsidiary = turn.subsidiary.id;
      }

      return turn;
    },
    /*async closeLocalTurnAndSync() {
      this.updateTurn();
      try {
        let closed = await this.$pos.closeTurn(this.turn);
        return closed;
      } catch (e) {
        console.error(e);
        return false;
      }
    },*/
    async closeTurn() {
      let data = this.getData();
      this.turnToPrint = this.$store.getters.turn;
      let closed = await this.$pos.closeTurn(data);
      if (closed) {
        this.print = true;
        this.$store.commit("setTurn", null);
      }
    },
    printTurn() {
      return this.$pos.printTurn(this.turnToPrint);
    },
  },
};
</script>
